/**
 * Black Trident – Holster Finder
 * Shopify Web Component
 *
 * Das HTML-Skeleton wird vom Liquid geliefert (holster-finder.liquid).
 * Dieses Script übernimmt ausschliesslich die Logik.
 *
 * Desktop-Panel: Tom Select, via [data-hf-input-set]
 * Mobile-Drawer: native <select>, registriert on drawer-open via [data-hf-filter-drawer]
 *
 * Voraussetzung: Tom Select muss vor diesem Script geladen sein.
 */

class HolsterFinder extends HTMLElement {
  connectedCallback() {
    this._indexUrl            = this.getAttribute("index-url");
    this._ctaLabel            = this.getAttribute("cta-label") || "Jetzt konfigurieren →";
    this._availableLabel      = this.getAttribute("label-available");
    this._unavailableLabel    = this.getAttribute("label-unavailable");
    this._notAvailableMessage = this.getAttribute("not-available-message");
    this._countLabelTotal     = this.getAttribute("label-count-total")  || "{{count}} Modelle";
    this._countLabelFound     = this.getAttribute("label-count-found")  || "{{count}} Modelle gefunden";
    this._countLabelHidden    = this.getAttribute("label-count-hidden") || "- {{count}} ausgeblendet";
    this._countLabelZero      = this.getAttribute("label-count-zero")   || "Keine Modelle gefunden";
    this._selectionEmptyLabel = this.getAttribute("label-selection-empty") || "Noch keine Waffe ausgewählt";
    this._selectionEmptyHint  = this.getAttribute("label-selection-empty-hint") || "Nach deiner Auswahl zeigen wir dir nur kompatible Holster.";
    this._noLightLabel        = this.getAttribute("label-no-light") || "Kein Licht";
    this._emptyLabel          = this.getAttribute("label-empty") || "Kein Holster für {{selection}} in dieser Kategorie verfügbar.";


    this.isTouch = false;

    this._state = {
      index:        null,
      manufacturer: "",
      model:        "",
      light:        "",
      lastEntries:  [],
    };

    // Desktop Input-Sets (Tom Select)
    this._inputSets = [...this.querySelectorAll("[data-hf-input-set]")].map(set => ({
      manufacturer: set.querySelector("[data-hf-manufacturer]"),
      model:        set.querySelector("[data-hf-model]"),
      light:        set.querySelector("[data-hf-light]"),
      reset:        set.querySelector("[data-hf-reset]"),
      filterActive: set.querySelector("[data-hf-active]"),
      submit:       set.querySelector("[data-hf-submit]"),
    }));

    // Fallback: kein data-hf-input-set vorhanden
    if (this._inputSets.length === 0) {
      this._inputSets = [{
        manufacturer: this.querySelector("[data-hf-manufacturer]"),
        model:        this.querySelector("[data-hf-model]"),
        light:        this.querySelector("[data-hf-light]"),
        reset:        this.querySelector("[data-hf-reset]"),
        filterActive: this.querySelector("[data-hf-active]"),
        submit:       this.querySelector("[data-hf-submit]"),
      }];
    }

    // Einmalige Elemente
    this._els = {
      status: this.querySelector("[data-hf-status]"),
      cards:  this.querySelector("[data-hf-cards]"),
    };

    this.statusDot = this.querySelector("[data-hf-status-dot]");

    // Tom Select Instanzen pro Desktop-Set
    this._ts = this._inputSets.map(() => ({
      manufacturer: null,
      model:        null,
      light:        null,
    }));

    // Event-Listener für Desktop-Sets
    this._inputSets.forEach((set, i) => {
      set.reset?.addEventListener("click", () => this._reset());
      set.filterActive?.addEventListener("change", (e) => {
        this._filterUnavailable(e.detail.checked);
        this._inputSets.forEach((s, j) => {
          if (j !== i && s.filterActive) s.filterActive.checked = e.detail.checked;
        });
      });
      set.submit?.addEventListener("click", () => this._findAndRender());
    });

    // Mobile Drawer — native selects, registriert on drawer-open
    this._drawerEl = this.querySelector("[data-hf-filter-drawer]");
    if (this._drawerEl) {
      this._drawerEl.addEventListener("drawer-open",  () => this._onDrawerOpen());
      this._drawerEl.addEventListener("drawer-close", () => this._onDrawerClose());
    }

    if (this._isTouch()) {
      document.documentElement.classList.add("touch-device");
      this.isTouch = true;
    }

    this._loadData();
  }

  disconnectedCallback() {
    this._destroyAll();
  }

  _isTouch() {
    return 'ontouchstart' in window;
  }

  /* ─── Drawer Handling ───────────────────────────────────────────── */

  _onDrawerOpen() {
    // Drawer klont den Inhalt — geklonte Selects im _body registrieren
    const body = this._drawerEl._body;
    if (!body) return;

    const mfEl    = body.querySelector("[data-hf-manufacturer]");
    const moEl    = body.querySelector("[data-hf-model]");
    const lEl     = body.querySelector("[data-hf-light]");
    const resetEl = body.querySelector("[data-hf-reset]");
    const filterEl = body.querySelector("[data-hf-active]");

    // Aktuellen State in Drawer-Selects synchronisieren
    const st = this._state;
    if (mfEl) mfEl.value = st.manufacturer;
    if (moEl) moEl.value = st.model;
    if (lEl)  lEl.value  = st.light;

    // Optionen befüllen
    if (mfEl) this._populateNativeSelect(mfEl,
      this._manufacturerIndices().map(i => this._s(i)).sort((a, b) => a.localeCompare(b, "de")),
      mfEl.getAttribute("data-placeholder") || "Hersteller auswählen",
      false,
      st.manufacturer
    );

    if (st.manufacturer) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);
      const models = (this._state.index.mm?.[String(mIdx)] ?? [])
        .map(i => this._s(i)).sort((a, b) => a.localeCompare(b, "de"));
      if (moEl) this._populateNativeSelect(moEl, models,
        moEl.getAttribute("data-placeholder") || "Modell auswählen", false, st.model);
    } else {
      if (moEl) this._populateNativeSelect(moEl, [],
        moEl.getAttribute("data-placeholder-empty") || "Bitte zuerst Hersteller wählen", true, "");
    }

    if (st.manufacturer && st.model) {
      const mIdx  = this._getManufacturerIdx(st.manufacturer);
      const moIdx = this._getModelIdx(mIdx, st.model);
      const lights = (this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? []).map(i => this._s(i));
      if (lEl) this._populateNativeSelect(lEl, lights,
        lEl.getAttribute("data-placeholder") || "Licht-/Lasermodul auswählen", false, st.light);
    } else {
      if (lEl) this._populateNativeSelect(lEl, [],
        lEl.getAttribute("data-placeholder-empty") || "Bitte zuerst Modell wählen", true, "");
    }

    if (filterEl) filterEl.checked = this.hasAttribute("data-filter-unavailable");

    // Alten Button entfernen falls vorhanden (bei erneutem Öffnen)
    body.querySelector("[data-hf-drawer-show]")?.remove();

    // "X Holster anzeigen" Button erstellen
    const showBtn = document.createElement("button");
    showBtn.className = "hf-drawer-show-results";
    showBtn.setAttribute("data-hf-drawer-show", "");
    showBtn.style.display = "none";

    // In den Utility-Footer vor den Reset-Button einsetzen (Fallback: Body-Ende)
    const footerEl = body.querySelector(".hf-utility-footer");
    const resetInFooter = footerEl?.querySelector("[data-hf-reset]");
    if (footerEl && resetInFooter) {
      footerEl.insertBefore(showBtn, resetInFooter);
    } else if (footerEl) {
      footerEl.insertBefore(showBtn, footerEl.firstChild);
    } else {
      body.appendChild(showBtn);
    }

    const updateShowBtn = () => {
      const st = this._state;
      const complete = st.manufacturer && st.model && st.light;
      showBtn.style.display = complete ? "" : "none";
      
      if (complete) {
        const count = this._state.lastEntries?.length ?? 0;
        const label = this.getAttribute("label-drawer-show") || "{{count}} Holster anzeigen";
        showBtn.textContent = label.replace("{{count}}", count);
      }
    };

    updateShowBtn();

    showBtn.addEventListener("click", () => {
      // Filter-Switch aktivieren damit nur verfügbare Holster sichtbar sind
      this._filterUnavailable(true);
      this._inputSets.forEach(s => {
        if (s.filterActive) s.filterActive.checked = true;
      });

      this._drawerEl.hide();
      // Zu den Cards scrollen
      const cards = this._els.cards;
      if (cards) {
        setTimeout(() => {
          cards.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    });

    // Event-Listener auf geklonten Selects
    mfEl?.addEventListener("change", e => {
      this._onManufacturerChange(e.target.value, -1);
      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    moEl?.addEventListener("change", e => {
      this._onModelChange(e.target.value, -1);
      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    lEl?.addEventListener("change", e => {
      this._onLightChange(e.target.value, -1);
      // State ist jetzt gesetzt — Button aktualisieren
      updateShowBtn();
    });

    resetEl?.addEventListener("click", () => {
      this._reset();
      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    filterEl?.addEventListener("change", e => {
      this._filterUnavailable(e.detail?.checked ?? e.target.checked);
      this._inputSets.forEach(s => {
        if (s.filterActive) s.filterActive.checked = e.detail?.checked ?? e.target.checked;
      });
    });
  }

  _onDrawerClose() {
    // Nichts nötig — geklonte Elemente verschwinden mit dem Drawer-Body
  }

  _syncDrawerSelects(body) {
    // Nach State-Änderungen Drawer-Selects aktuell halten
    const st    = this._state;
    const mfEl  = body.querySelector("[data-hf-manufacturer]");
    const moEl  = body.querySelector("[data-hf-model]");
    const lEl   = body.querySelector("[data-hf-light]");

    if (moEl && st.manufacturer) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);
      const models = (this._state.index.mm?.[String(mIdx)] ?? [])
        .map(i => this._s(i)).sort((a, b) => a.localeCompare(b, "de"));
      this._populateNativeSelect(moEl, models,
        moEl.getAttribute("data-placeholder") || "Modell auswählen", false, st.model);
    } else if (moEl) {
      this._populateNativeSelect(moEl, [],
        moEl.getAttribute("data-placeholder-empty") || "Bitte zuerst Hersteller wählen", true, "");
    }

    if (lEl && st.manufacturer && st.model) {
      const mIdx  = this._getManufacturerIdx(st.manufacturer);
      const moIdx = this._getModelIdx(mIdx, st.model);
      const lights = (this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? []).map(i => this._s(i));
      this._populateNativeSelect(lEl, lights,
        lEl.getAttribute("data-placeholder") || "Licht-/Lasermodul auswählen", false, st.light);
    } else if (lEl) {
      this._populateNativeSelect(lEl, [],
        lEl.getAttribute("data-placeholder-empty") || "Bitte zuerst Modell wählen", true, "");
    }
  }

  _populateNativeSelect(el, values, placeholder, disabled, selectedValue) {
    el.innerHTML = "";
    const first = document.createElement("option");
    first.value = "";
    first.textContent = placeholder;
    el.appendChild(first);
    for (const v of values) {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      if (v === selectedValue) opt.selected = true;
      el.appendChild(opt);
    }
    el.disabled = disabled;
    if (selectedValue) el.value = selectedValue;
  }

  _updateSelectionStatus() {
    const els = this.querySelectorAll("[data-hf-selection-status]");
    if (!els.length) return;

    const st = this._state;

    let html;
    if (!st.manufacturer) {
      html =
        `<div class="hf-selection-status-unselected">` +
          `<strong>${this._esc(this._selectionEmptyLabel)}</strong>` +
          `<span>${this._esc(this._selectionEmptyHint)}</span>` +
        `</div>`;
    } else {
      const parts = [st.manufacturer, st.model].filter(Boolean);
      const light = st.light
        ? (st.light === "Keines" ? this._noLightLabel : st.light)
        : null;

      html = `<span class="hf-status-chip">${this._esc(parts.join(" "))}</span>`;
      if (light) html += `<span class="hf-status-chip">${this._esc(light)}</span>`;
    }

    els.forEach(el => {
      el.innerHTML = html;
      if (!st.manufacturer) el.setAttribute("data-empty", "");
      else el.removeAttribute("data-empty");
    });
  }

  /* ─── Tom Select lifecycle ──────────────────────────────────────── */

  _tsOptions(key, setIndex, placeholder) {
    return {
      create:           false,
      allowEmptyOption: false,
      placeholder:      placeholder,
      searchField:      ["text"],
      sortField:        false,
      maxOptions:       null,   // alle Einträge anzeigen; Default 50 schnitt Hersteller mit >50 Modellen (SIG Sauer: 53) im Dropdown ab
      selectOnTab:      true,
      onFocus() {
        const item = this.control.querySelector(".item");
        if (item) item.style.display = "none";
        this.setTextboxValue("");
        this.refreshOptions(false);
      },
      onItemAdd() {
        const item = this.control.querySelector(".item");
        if (item) item.style.display = "";
        this.setTextboxValue("");
        this.blur();
      },
      onBlur() {
        const item = this.control.querySelector(".item");
        if (item) item.style.display = "";
      },
      onChange: (value) => {
        if (key === "manufacturer") this._onManufacturerChange(value, setIndex);
        if (key === "model")        this._onModelChange(value, setIndex);
        if (key === "light")        this._onLightChange(value, setIndex);
      },
    };
  }

  _initAll() {
    this._inputSets.forEach((set, i) => {
      const p = {
        manufacturer: set.manufacturer?.getAttribute("data-placeholder") || "Hersteller auswählen",
        model:        set.model?.getAttribute("data-placeholder")        || "Modell auswählen",
        light:        set.light?.getAttribute("data-placeholder")        || "Licht-/Lasermodul auswählen",
      };

      if (!window.TomSelect) {
        set.manufacturer?.addEventListener("change", e => this._onManufacturerChange(e.target.value, i));
        set.model?.addEventListener("change",        e => this._onModelChange(e.target.value, i));
        set.light?.addEventListener("change",        e => this._onLightChange(e.target.value, i));
        return;
      }

      if (set.manufacturer) this._ts[i].manufacturer = new TomSelect(set.manufacturer, this._tsOptions("manufacturer", i, p.manufacturer));
      if (set.model)        this._ts[i].model        = new TomSelect(set.model,        this._tsOptions("model",        i, p.model));
      if (set.light)        this._ts[i].light        = new TomSelect(set.light,        this._tsOptions("light",        i, p.light));
    });
  }

  _destroyAll() {
    this._ts.forEach(ts => {
      for (const t of Object.values(ts)) {
        if (t) t.destroy();
      }
    });
    this._ts = this._inputSets.map(() => ({ manufacturer: null, model: null, light: null }));
  }

  /* ─── Populate helper — alle Desktop-Sets synchron befüllen ────── */

  _populate(key, placeholder, values, disabled) {
    this._inputSets.forEach((set, i) => {
      const ts = this._ts[i][key];
      const el = set[key];

      if (ts) {
        ts.clear(true);
        ts.clearOptions();
        for (const v of values) ts.addOption({ value: v, text: v });
        ts.setValue("", true);
        disabled ? ts.disable() : ts.enable();
        ts.refreshOptions(false);
      } else if (el) {
        el.innerHTML = "";
        const first = document.createElement("option");
        first.value = "";
        first.textContent = placeholder;
        el.appendChild(first);
        for (const v of values) {
          const opt = document.createElement("option");
          opt.value = v;
          opt.textContent = v;
          el.appendChild(opt);
        }
        el.disabled = disabled;
      }
    });
  }

  _syncValue(key, value) {
    this._inputSets.forEach((set, i) => {
      const ts = this._ts[i][key];
      const el = set[key];
      if (ts) {
        ts.setValue(value, true);
        const item = ts.control.querySelector(".item");
        if (item) item.style.display = value ? "" : "none";
      } else if (el) {
        el.value = value;
      }
    });
  }

  _setSubmitState(enabled) {
    this._inputSets.forEach(set => {
      if (!set.submit) return;
      set.submit.classList.toggle("hf-submit--disabled", !enabled);
      set.submit.disabled = !enabled;
    });
  }

  /* ─── Data loading ──────────────────────────────────────────────── */

  async _loadData() {
    this._setStatus("Daten werden geladen…");
    try {
      const today = new Date().toISOString().split("T")[0];
      const indexRes = await fetch(this._indexUrl + "?v=" + today);
      if (!indexRes.ok) throw new Error(`Index-Datei nicht erreichbar (${indexRes.status})`);
      this._state.index = await indexRes.json();

      this._bootSelects();
      this._renderCards([]);
      this.dispatchEvent(new CustomEvent("holster-finder:ready", { bubbles: true }));
      this._setStatus("");
      //this._initVideoHover();
    } catch (err) {
      this._setStatus(`Fehler: ${err.message}`);
      const dot = this.statusDot?.querySelector(".hf-status-dot");
      if (dot) {
        dot.classList.remove("online");
        dot.classList.add("offline");
      }
      console.error("[holster-finder]", err);
    }
  }

  /* ─── Index helpers ─────────────────────────────────────────────── */

  _s(i)   { return this._state.index.s[i] ?? ""; }
  _h(i)   { return this._state.index.h[i] ?? ""; }
  _f(i)   { return this._state.index.f[i] ?? ""; }

  _mmKey(mIdx, moIdx)      { return `${mIdx}|${moIdx}`; }
  _rKey(mIdx, moIdx, lIdx) { return `${mIdx}|${moIdx}|${lIdx}`; }

  _manufacturerIndices() { return this._state.index.m ?? []; }

  _getManufacturerIdx(label) {
    for (const idx of this._manufacturerIndices()) {
      if (this._s(idx) === label) return idx;
    }
    return null;
  }

  _getModelIdx(mIdx, label) {
    const modelIndices = this._state.index.mm?.[String(mIdx)] ?? [];
    for (const idx of modelIndices) {
      if (this._s(idx) === label) return idx;
    }
    return null;
  }

  _getLightIdx(mIdx, moIdx, label) {
    const lightIndices = this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? [];
    for (const idx of lightIndices) {
      if (this._s(idx) === label) return idx;
    }
    return null;
  }

  /* ─── Boot ──────────────────────────────────────────────────────── */

  _bootSelects() {
    const manufacturers = this._manufacturerIndices()
      .map(i => this._s(i))
      .sort((a, b) => a.localeCompare(b, "de"));

    this._initAll();

    const first = this._inputSets[0];
    const p = {
      manufacturer: first.manufacturer?.getAttribute("data-placeholder") || "Hersteller auswählen",
      model:        first.model?.getAttribute("data-placeholder")        || "Bitte zuerst Hersteller wählen",
      light:        first.light?.getAttribute("data-placeholder")        || "Bitte zuerst Modell wählen",
    };

    this._populate("manufacturer", p.manufacturer, manufacturers, false);
    this._populate("model",        p.model,        [],            true);
    this._populate("light",        p.light,        [],            true);
  }

  _reset() {
    const st = this._state;
    st.manufacturer = "";
    st.model = "";
    st.light = "";
    st.lastEntries = [];

    this._ts.forEach(ts => {
      ts.manufacturer?.clear();
      ts.model?.clear();
      ts.light?.clear();
    });

    const first = this._inputSets[0];
    const p = {
      model: first.model?.getAttribute("data-placeholder-empty") || "Bitte zuerst Hersteller wählen",
      light: first.light?.getAttribute("data-placeholder-empty") || "Bitte zuerst Modell wählen",
    };

    this._populate("model", p.model, [], true);
    this._populate("light", p.light, [], true);
    this._setStatus("");
    this._setSubmitState(false);

    const cards = this._els.cards?.querySelectorAll("[data-holster-id]");
    if (cards) {
      for (const card of cards) {
        card.href = card.dataset.originalHref || "#";
        card.style.pointerEvents = "";
      }
    }

    this._inputSets.forEach(set => {
      if (set.filterActive) set.filterActive.checked = false;
    });
    this.removeAttribute("data-filter-unavailable");

    this._renderCards([]);
    this._updateSelectionStatus();
  }

  _filterUnavailable(hide) {
    this.toggleAttribute("data-filter-unavailable", hide);
    if (this._state.lastEntries?.length > 0) {
      this._renderCards(this._state.lastEntries);
    }
  }

  /* ─── Video hover ───────────────────────────────────────────────── */

  _initVideoHover() {
    const container = this._els.cards;
    if (!container) return;

    container.querySelectorAll(".hf-card:has(video.hf-card-video)").forEach(card => {
      const video = card.querySelector("video.hf-card-video");
      if (!video) return;

      card.addEventListener("mouseenter", () => {
        if (video.readyState === 0) video.load();
        const tryPlay = () => video.play().catch(() => {});
        if (video.readyState >= 3) tryPlay();
        else video.addEventListener("canplay", tryPlay, { once: true });
      });

      card.addEventListener("mouseleave", () => {
        video.pause();
        video.currentTime = 0;
      });
    });
  }

  /* ─── Change handlers ───────────────────────────────────────────── */

  _onManufacturerChange(value, sourceSetIndex) {
    const st = this._state;
    st.manufacturer = value || "";
    st.model = "";
    st.light = "";

    const first = this._inputSets[0];
    const p = {
      model: first.model?.getAttribute("data-placeholder-empty") || "Bitte zuerst Hersteller wählen",
      light: first.light?.getAttribute("data-placeholder-empty") || "Bitte zuerst Modell wählen",
    };

    this._populate("model", p.model, [], true);
    this._populate("light", p.light, [], true);
    this._renderCards([]);
    this._setStatus("");
    this._setSubmitState(false);
    this._updateSelectionStatus();

    // Desktop-Sets synchronisieren (nicht die Quelle)
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) return;
      const ts = this._ts[i].manufacturer;
      if (ts) ts.setValue(value, true);
      else if (set.manufacturer) set.manufacturer.value = value;
    });

    if (!st.manufacturer) return;

    const mIdx = this._getManufacturerIdx(st.manufacturer);
    const models = (this._state.index.mm?.[String(mIdx)] ?? [])
      .map(i => this._s(i))
      .sort((a, b) => a.localeCompare(b, "de"));

    const pModel = first.model?.getAttribute("data-placeholder") || "Modell auswählen";
    this._populate("model", pModel, models, false);
  }

  _onModelChange(value, sourceSetIndex) {
    const st = this._state;
    st.model = value || "";
    st.light = "";

    const first = this._inputSets[0];
    const pLight = first.light?.getAttribute("data-placeholder-empty") || "Bitte zuerst Modell wählen";
    this._populate("light", pLight, [], true);
    this._setStatus("");
    this._setSubmitState(false);

    // Desktop-Sets synchronisieren
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) return;
      const ts = this._ts[i].model;
      if (ts) ts.setValue(value, true);
      else if (set.model) set.model.value = value;
    });

    if (!st.manufacturer || !st.model) {
      this._renderCards([]);
      return;
    }

    const mIdx  = this._getManufacturerIdx(st.manufacturer);
    const moIdx = this._getModelIdx(mIdx, st.model);
    const lights = (this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? [])
      .map(i => this._s(i));

    const pLightActive = first.light?.getAttribute("data-placeholder") || "Licht-/Lasermodul auswählen";
    this._populate("light", pLightActive, lights, false);

    if (lights.length === 1 && lights[0] === "Keines") {
      this._syncValue("light", "Keines");
      st.light = "Keines";
      this._setSubmitState(true);
      this._findAndRender();
    } else {
      this._renderCards([]);
    }

    this._updateSelectionStatus();
  }

  _onLightChange(value, sourceSetIndex) {
    const st = this._state;
    st.light = value || "";

    this._setSubmitState(!!st.light);

    // Desktop-Sets synchronisieren
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) return;
      const ts = this._ts[i].light;
      if (ts) ts.setValue(value, true);
      else if (set.light) set.light.value = value;
    });

    if (!st.manufacturer || !st.model || !st.light) {
      this._renderCards([]);
      this._setStatus("");
      return;
    }

    this._findAndRender();
    this._updateSelectionStatus();
  }

  /* ─── Lookup & render ───────────────────────────────────────────── */

  _findAndRender() {
    const st = this._state;
    const mIdx  = this._getManufacturerIdx(st.manufacturer);
    const moIdx = this._getModelIdx(mIdx, st.model);
    const lIdx  = this._getLightIdx(mIdx, moIdx, st.light);

    if (mIdx === null || moIdx === null || lIdx === null) {
      this._setStatus("Keine Einträge für diese Kombination im Index.");
      this._renderCards([]);
      return;
    }

    const entries = this._state.index.r?.[this._rKey(mIdx, moIdx, lIdx)] ?? [];
    this._setStatus(`${entries.length} Holster gefunden für: ${st.manufacturer} · ${st.model} · ${st.light}`);
    this._state.lastEntries = entries;

    // Alle 3 Felder gewählt: Filter automatisch aktivieren und nur verfügbare
    // Holster einblenden. Attribut vor dem Rendern setzen, damit Karten-Sichtbarkeit
    // und Zähler direkt stimmen. Switches (Desktop + Mobile-Bar) visuell mitsetzen.
    this.setAttribute("data-filter-unavailable", "");
    this._inputSets.forEach(set => {
      if (set.filterActive) set.filterActive.checked = true;
    });

    this._renderCards(entries);
  }

  _buildUrl(entry) {
    const st = this._state;
    const [holsterIdx, rawModelIdx, rawLightIdx, lightFieldIdx, mfFieldIdx, modelFieldIdx] = entry;

    const holsterId = this._h(holsterIdx);
    const card = this._els.cards?.querySelector(`[data-holster-id="${holsterId}"]`);
    const baseUrl = card?.dataset.originalHref;
    if (!baseUrl) return "#";

    const url = new URL(baseUrl);

    const mfField    = this._f(mfFieldIdx);
    const modelField = this._f(modelFieldIdx);
    const lightField = this._f(lightFieldIdx);
    const rawModel   = this._s(rawModelIdx);
    const rawLight   = this._s(rawLightIdx);

    // Roher Hersteller-Wert für den Deep-Link kommt aus dem Index (mr-Map,
    // display -> DPO-Feldwert). Wird beim Build aus den DPO-Daten abgeleitet,
    // damit es keine separat gepflegte Map mehr braucht.
    const mrMap      = this._state.index?.mr || {};
    const rawMf      = mrMap[st.manufacturer] ?? st.manufacturer;

    const isFieldKey = v => typeof v === "string" && /^F\d+$/.test(v);

    if (isFieldKey(mfField)    && rawMf)    url.searchParams.set(mfField,    rawMf);
    if (isFieldKey(modelField) && rawModel) url.searchParams.set(modelField, rawModel);
    if (isFieldKey(lightField) && rawLight) url.searchParams.set(lightField, rawLight);

    return url.toString()
      .replace(/\+/g, "%20")
      .replace(/%28/g, "(")
      .replace(/%29/g, ")");
  }

  _renderCards(entries) {
    const container = this._els.cards;
    if (!container) return;

    const availableIds = new Set(entries.map(e => this._h(e[0])));
    const hasSearch    = entries.length > 0 || this._state.light;
    const cards        = container.querySelectorAll("[data-holster-id]");

    for (const card of cards) {
      const holsterId   = card.dataset.holsterId;
      const isAvailable = !hasSearch || availableIds.has(holsterId);

      card.classList.toggle("hf-card--available",   hasSearch && isAvailable);
      card.classList.toggle("hf-card--unavailable", hasSearch && !isAvailable);

      if (isAvailable) {
        const entry = entries.find(e => this._h(e[0]) === holsterId);
        const url = entry ? this._buildUrl(entry) : (card.dataset.originalHref || "#");
        card.href = url;
        card.style.pointerEvents = "";
        // Drawer-CTA (.drawer-holster-link) mit derselben Deep-Link-URL versorgen
        document.dispatchEvent(new CustomEvent("holster:available", {
          detail: { holsterId, url },
        }));
      } else {
        card.href = "#";
        card.style.pointerEvents = "none";
        // Drawer-CTA auf Basis-URL zurücksetzen (kein veralteter Deep-Link)
        document.dispatchEvent(new CustomEvent("holster:available", {
          detail: { holsterId, url: card.dataset.originalHref || "#" },
        }));
      }
    }

    const groups = container.querySelectorAll(".hf-group");

    // Selektions-Text für den Empty-State (z.B. "OPOS Venator · Glock GTL-2")
    const stSel = this._state;
    let emptyCombo = [stSel.manufacturer, stSel.model].filter(Boolean).join(" ");
    if (stSel.light && stSel.light !== "Keines") emptyCombo += " · " + stSel.light;

    for (const group of groups) {
      const carryKey = group.getAttribute("data-hf-group");
      const heading  = container.querySelector(`.hf-group-heading[data-hf-group-heading="${carryKey}"]`);
      const wrapper  = heading ? heading.closest(".hf-group-heading-wrapper") : null;
      if (!wrapper) continue;
      const countEl  = wrapper.querySelector(".hf-group-heading-count");

      const total     = group.querySelectorAll("[data-holster-id]").length;
      const available = group.querySelectorAll(".hf-card--available").length;
      const hidden    = group.querySelectorAll(".hf-card--unavailable").length;
      const filterOn  = this.hasAttribute("data-filter-unavailable");

      if (heading) {
        //heading.classList.toggle("hf-group-heading--unavailable", hasSearch && available === 0);
      }

      if (countEl) {
        if (!hasSearch) {
          countEl.textContent = this._countLabelTotal.replace("{{count}}", total);
        } else if (available === 0) {
          countEl.textContent = this._countLabelZero;
        } else if (filterOn && hidden > 0) {
          countEl.textContent =
            this._countLabelFound.replace("{{count}}", available) + " " +
            this._countLabelHidden.replace("{{count}}", hidden);
        } else {
          countEl.textContent = this._countLabelFound.replace("{{count}}", available);
        }
      }

      // No-Match-Hinweis: zeigen, wenn bei aktivem Filter kein verfügbares Modell übrig ist.
      // (Filter aus -> gesperrte Karten füllen die Fläche, dann kein Hinweis.)
      const emptyEl = container.querySelector(`.hf-group-no-match[data-hf-group-empty="${carryKey}"]`);
      if (emptyEl) {
        const showEmpty = hasSearch && filterOn && available === 0;
        emptyEl.style.display = showEmpty ? "block" : "none";
        if (showEmpty) {
          const textEl = emptyEl.querySelector("[data-hf-empty-text]");
          if (textEl) {
            textEl.innerHTML = this._emptyLabel.replace(
              /\{\{\s*selection\s*\}\}/g,
              `<span>${this._esc(emptyCombo)}</span>`
            );
          }
        }
      }
    }
  }

  /* ─── Utilities ─────────────────────────────────────────────────── */

  _setStatus(msg) {
    if (this._els.status) this._els.status.textContent = msg;
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}

customElements.define("holster-finder", HolsterFinder);